import React, { FC, useContext, useEffect, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  createTheme,
  CssBaseline,
  hexToRgb,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SurveyService from '../../services/SurveyService';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import CustomQuestionDropdown from './CustomQuestionDropdown';
import CustomQuestionSingle from './CustomQuestionSingle';
import CustomQuestionText from './CustomQuestionText';
import CustomQuestionScalenps from './CustomQuestionScalenps';
import Autolink from '../Autolink/Autolink';
import { useSearchParams } from 'react-router-dom';

const CustomSurveyContent: FC<ISurvey> = (props: ISurvey) => {
  const {
    primaryColor,
    secondaryColor,
    platform,
    platformUrl,
    platformName,
    id,
    questions,
    title,
    titleNegative,
    outroText,
    outroTextNegative,
    customFont,
    customFontFormat,
    score,
    introText,
    feedbackActive,
    feedbackTitle,
    feedbackText,
    answers,
    contactEmail,
  } = props;

  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [responseId, setResponseId] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [actionCount, setActionCount] = useState<number>(0);
  const [currentAnswer, setCurrentAnswer] = useState<string>('');
  const { setCurrentView } = useContext(CurrentViewContext);
  const [submitCount, setSubmitCount] = useState<number>(0);
  const [finished, setFinished] = useState<boolean>(false);
  const [answersObject, setAnswersObject] = useState<any>({});

  const hasAnswer = (questionId: string) => {
    if (answersObject[questionId] && answersObject[questionId].feedback) {
      return true;
    }
    return false;
  };

  const getAnswer = (questionId: string) => {
    if (answersObject[questionId]) {
      return answersObject[questionId].feedback;
    }
    return '';
  };

  const setAnswer = (questionId: string, value: string, step: number) => {
    setCurrentAnswer(value);
    setAnswersObject((prev: any) => {
      const newAnswers = prev;

      if (!newAnswers[questionId]) {
        newAnswers[questionId] = {
          questionId: questionId,
          question: '',
          answer: 1,
          feedback: value,
        };
      } else {
        newAnswers[questionId].feedback = value;
      }
      return newAnswers;
    });
  };

  useEffect(() => {
    if (submitCount > 0) {
      try {
        (async () => {
          const sendAnswers: any[] = [];
          Object.keys(answersObject).forEach((key: string) => {
            sendAnswers.push(answersObject[key]);
          });
          if (responseId) {
            sendAnswers.forEach((sa: any) => (sa.responseId = responseId));
          }
          if (sendAnswers.length > 0) {
            const preview = searchParams.get('preview');
            if (preview !== 'true') {
              const answers = await SurveyService.saveCustomSurveyAnswer(id, sendAnswers);
              if (answers.length > 0) {
                setResponseId(answers[0].ResponseID);
              }
            }
          }
        })();
      } catch (error) {
        console.error('Error saving survey:', error);
      }
    }
  }, [submitCount]);

  const saveSurvey = async () => {
    setSubmitCount(submitCount + 1);
  };

  useEffect(() => {
    setCurrentView(t('survey'));
    document.title = `Reputativ ${t('survey')}`;
  }, [setCurrentView, t]);

  const showLastPage = () => {
    const questionCount = questions?.length || 0;
    return questionCount === currentStep;
  };

  const isLastQuestion = () => {
    const questionCount = questions?.length || 0;
    const isLast = currentStep === questionCount - 1;
    return isLast;
  };

  const canGoBack = () => {
    return -1 != currentStep && !finished;
  };

  const canGoNext = () => {
    return !showLastPage() && !finished;
  };

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  const customTheme = createTheme({
    palette: {
      primary: {
        main: primaryColor ? primaryColor : theme.palette.primary.main,
      },
      divider: secondaryColor ? alpha(hexToRgb(secondaryColor), 0.2) : theme.palette.divider,
    },
    typography: {
      fontFamily: customFont && customFontFormat ? `"${id}"` : '"Arial", "Helvetica", sans-serif',
      fontWeightBold: 700,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides:
          customFont && customFontFormat
            ? `
           @font-face {
            weight: 700;
            font-family: "${id}";
            src: url(${customFont}) format("${getFontFormatByFileExtension(
                customFontFormat as FontFileExtensions
              )}");
           }
        `
            : undefined,
      },
    },
  });

  const goNext = async (step: number) => {
    setActionCount(actionCount + 3);
    if (step > -1) {
      await saveSurvey();
    }
    if (isLastQuestion()) {
      setFinished(true);
    }
    setCurrentStep(step + 1);
  };

  const goBack = (step: number) => {
    setActionCount(actionCount + 7);
    setCurrentStep(step - 1);
  };

  const handleQuestionChange = async (
    questionId: string,
    value: string,
    step: number,
    proceed: boolean
  ) => {
    // this causes the form to refresh and show the next button in the correct state
    setActionCount(actionCount + 1);
    setAnswer(questionId, value, step);
    if (proceed) {
      goNext(step);
    }
  };

  const renderQuestion = (step: number) => {
    if (!questions || !questions[step]) {
      return null;
    }
    const question = questions[step];
    return (
      <ThemeProvider theme={customTheme}>
        <CssBaseline />
        <Box key={question.questionId}>
          <Typography
            variant="h5"
            sx={{
              color: primaryColor,
              marginBottom: theme.spacing(5),
              fontFamily: customFont,
              alignItems: 'center',
              fontWeight: 'bold',
              fontSize: theme.spacing(4),
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: secondaryColor,
              marginBottom: theme.spacing(3),
              fontFamily: customFont,
              alignItems: 'center',
            }}
          >
            {question.question}
          </Typography>
          {'CUSTOMDROPDOWN' == (question.type ?? '') && (
            <CustomQuestionDropdown
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
            />
          )}
          {'CUSTOMSCALENPS' == (question.type ?? '') && (
            <CustomQuestionScalenps
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              customFont={customFont}
              step={step}
            />
          )}
          {'CUSTOMYESNO' == (question.type ?? '') && (
            <CustomQuestionSingle
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
            />
          )}
          {'CUSTOMSINGLE' == (question.type ?? '') && (
            <CustomQuestionSingle
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
            />
          )}
          {'CUSTOMTEXT' == (question.type ?? '') && (
            <CustomQuestionText
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
            />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            textAlign: 'center',
            width: '100%',
            gap: theme.spacing(2),
            mt: theme.spacing(4),
          }}
        >
          {canGoBack() && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: primaryColor,
                width: '120px',
                padding: theme.spacing(2),
              }}
              onClick={() => goBack(step)}
            >
              {t('back')}
            </Button>
          )}
          {canGoNext() && (
            <Button
              variant="contained"
              disabled={!question.optional && !hasAnswer(question.questionId || '')}
              sx={{
                backgroundColor: primaryColor,
                width: '120px',
                padding: theme.spacing(2),
              }}
              onClick={async () => {
                if (isLastQuestion()) {
                  await saveSurvey();
                }
                goNext(step);
              }}
            >
              {t('next')}
            </Button>
          )}
        </Box>
      </ThemeProvider>
    );
  };

  const renderOutro = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: primaryColor,
            marginBottom: theme.spacing(5),
            fontWeight: 'bold',
            fontFamily: customFont,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: secondaryColor,
            marginBottom: theme.spacing(5),
            fontFamily: customFont,
            alignItems: 'center',
          }}
        >
          <Autolink text={outroText} />
        </Typography>
      </Box>
    );
  };

  const renderIntro = () => {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: primaryColor,
              marginBottom: theme.spacing(5),
              fontWeight: 'bold',
              fontFamily: customFont,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: secondaryColor,
              marginBottom: theme.spacing(5),
              fontFamily: customFont,
              alignItems: 'center',
            }}
          >
            <Autolink text={introText} />
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            textAlign: 'center',
            width: '100%',
            gap: theme.spacing(2),
            mt: theme.spacing(4),
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={async () => {
              if (isLastQuestion()) {
                await saveSurvey();
              }
              goNext(currentStep);
            }}
          >
            {t('next')}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        width: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        alignSelf: 'center',
        ml: isSmallScreen ? '1%' : '10%',
        mr: isSmallScreen ? '1%' : '10%',
        mt: 'auto',
      }}
    >
      {currentStep === -1 ? renderIntro() : null}
      {renderQuestion(currentStep)}
      {showLastPage() && finished ? renderOutro() : null}
    </Box>
  );
};

export default CustomSurveyContent;
